import React from 'react';
import { Phone, Mail, Globe, MapPin, Check, Layers, Crosshair, ShieldCheck, ArrowRight, Printer } from 'lucide-react';

/* =======================================================================
   PRINT MATERIALS PAGE
   Ulotka A5 (148 x 210 mm) — 2 strony (front + tył)
   Wizytówka 85 x 55 mm — 2 strony (front + tył)
   ======================================================================= */

const LogoBlock = ({ size = 'md', light = true }) => {
  const sz = { sm: 'text-[14px]', md: 'text-[20px]', lg: 'text-[34px]' }[size];
  const sub = { sm: 'text-[7px]', md: 'text-[9px]', lg: 'text-[11px]' }[size];
  return (
    <div className="flex flex-col leading-none">
      <span className={`font-black tracking-[0.18em] ${sz} ${light ? 'text-white' : 'text-[#0b0b0b]'}`}>
        LS<span className="text-[#e30613]">\</span>TOIT
      </span>
      <span className={`tracking-[0.32em] mt-0.5 ${sub} ${light ? 'text-white/60' : 'text-[#0b0b0b]/55'}`}>
        DACHY PŁASKIE
      </span>
    </div>
  );
};

/* ------------------------- ULOTKA A5 ------------------------- */

const FlyerFront = () => (
  <div className="a5 bg-[#0b0b0b] text-white relative overflow-hidden">
    {/* Hero image with overlay */}
    <div className="absolute inset-0">
      <img
        src="https://customer-assets.emergentagent.com/job_admiring-kepler-5/artifacts/r3zjxz5y_20210819_132023.jpg"
        alt=""
        className="w-full h-full object-cover opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/90" />
    </div>

    <div className="relative h-full flex flex-col p-[10mm]">
      <LogoBlock size="md" light />

      <div className="mt-[14mm] border-l-2 border-[#e30613] pl-[5mm]">
        <h1 className="font-black uppercase tracking-tight leading-[1.05] text-[19px]">
          Dachy płaskie,<br />
          które mają być<br />
          szczelne<br />
          <span className="text-[#e30613]">przez lata.</span>
        </h1>
        <p className="mt-[5mm] text-[9.5px] text-white/80 leading-[1.55] max-w-[90mm]">
          Specjalistyczne wykonanie dachów płaskich, tarasów
          i hydroizolacji w systemach EPDM, RESITRIX, HERTALAN,
          TPO/FPO i PVC.
        </p>
      </div>

      <div className="mt-auto">
        <div className="grid grid-cols-3 gap-[3mm] mb-[6mm]">
          <div className="border-l-2 border-[#e30613] pl-[3mm]">
            <p className="text-[6.5px] tracking-[0.18em] text-white/55 uppercase">Na rynku od</p>
            <p className="text-[13px] font-bold mt-0.5">2018</p>
          </div>
          <div className="border-l-2 border-[#e30613] pl-[3mm]">
            <p className="text-[6.5px] tracking-[0.18em] text-white/55 uppercase">Realizacje</p>
            <p className="text-[13px] font-bold mt-0.5">Wrocław +</p>
          </div>
          <div className="border-l-2 border-[#e30613] pl-[3mm]">
            <p className="text-[6.5px] tracking-[0.18em] text-white/55 uppercase">Systemy</p>
            <p className="text-[13px] font-bold mt-0.5">Premium</p>
          </div>
        </div>

        <div className="bg-[#e30613] px-[6mm] py-[5mm] flex items-center justify-between">
          <div>
            <p className="text-[7.5px] tracking-[0.24em] font-semibold">UMÓW OGLĘDZINY</p>
            <p className="text-[15px] font-black tracking-wide mt-[1mm]">+48 797 219 803</p>
          </div>
          <ArrowRight size={18} strokeWidth={2.5} />
        </div>
      </div>
    </div>
  </div>
);

const FlyerBack = () => (
  <div className="a5 bg-white text-[#0b0b0b] relative overflow-hidden">
    <div className="h-full flex flex-col p-[10mm]">
      <LogoBlock size="md" light={false} />

      {/* Systemy */}
      <div className="mt-[8mm]">
        <div className="flex items-center gap-2 mb-[4mm]">
          <span className="block w-[6mm] h-[1.5px] bg-[#e30613]" />
          <h3 className="font-bold tracking-[0.22em] text-[8px]">NASZE SYSTEMY</h3>
        </div>
        <div className="grid grid-cols-2 gap-[3mm] text-[8.5px]">
          <div className="border border-[#0b0b0b]/15 p-[3mm]">
            <p className="font-bold tracking-wide">EPDM / RESITRIX / HERTALAN</p>
            <p className="text-[#555] mt-[1mm] leading-tight">Syntetyczna guma, klejone, 40+ lat trwałości</p>
          </div>
          <div className="border border-[#0b0b0b]/15 p-[3mm]">
            <p className="font-bold tracking-wide">TPO / FPO / PVC BAUDER</p>
            <p className="text-[#555] mt-[1mm] leading-tight">Zgrzewane termoplastyki, hale i obiekty</p>
          </div>
        </div>
      </div>

      {/* Zakres */}
      <div className="mt-[7mm]">
        <div className="flex items-center gap-2 mb-[4mm]">
          <span className="block w-[6mm] h-[1.5px] bg-[#e30613]" />
          <h3 className="font-bold tracking-[0.22em] text-[8px]">ZAKRES USŁUG</h3>
        </div>
        <ul className="grid grid-cols-2 gap-x-[4mm] gap-y-[1.5mm] text-[8.5px]">
          {[
            'Dachy płaskie domów',
            'Tarasy nad pomieszczeniami',
            'Attyki, wpusty, przelewy',
            'Obróbki blacharskie',
            'Hydroizolacje EPDM',
            'Naprawy i przebudowy',
          ].map((t) => (
            <li key={t} className="flex items-start gap-1.5">
              <Check size={10} className="text-[#e30613] mt-[0.5mm] shrink-0" strokeWidth={3} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Pillars */}
      <div className="mt-[7mm] grid grid-cols-3 gap-[3mm]">
        {[
          { Icon: Layers, title: 'SYSTEM', text: 'Sprawdzone technologie: EPDM, RESITRIX, TPO, PVC.' },
          { Icon: Crosshair, title: 'DETAL', text: 'Najwięcej uwagi tam, gdzie najczęściej zawodzi.' },
          { Icon: ShieldCheck, title: 'GWARANCJA', text: 'Odpowiedzialność za efekt, nie tylko za metry.' },
        ].map(({ Icon, title, text }) => (
          <div key={title} className="border-t border-[#0b0b0b]/15 pt-[2mm]">
            <Icon size={14} strokeWidth={1.5} />
            <p className="font-bold tracking-[0.18em] text-[7.5px] mt-[2mm]">{title}</p>
            <p className="text-[7.5px] text-[#555] leading-[1.35] mt-[1mm]">{text}</p>
          </div>
        ))}
      </div>

      {/* Kontakt */}
      <div className="mt-auto bg-[#0b0b0b] text-white p-[5mm] grid grid-cols-3 gap-[3mm] items-center">
        <div className="flex items-center gap-[2mm]">
          <Phone size={12} className="text-[#e30613] shrink-0" strokeWidth={1.8} />
          <div>
            <p className="text-[6.5px] tracking-[0.22em] text-white/55">TELEFON</p>
            <p className="text-[9px] font-bold mt-[0.5mm]">+48 797 219 803</p>
          </div>
        </div>
        <div className="flex items-center gap-[2mm]">
          <Mail size={12} className="text-[#e30613] shrink-0" strokeWidth={1.8} />
          <div>
            <p className="text-[6.5px] tracking-[0.22em] text-white/55">E-MAIL</p>
            <p className="text-[8.5px] font-bold mt-[0.5mm]">kontakt@lstoit.pl</p>
          </div>
        </div>
        <div className="flex items-center gap-[2mm]">
          <Globe size={12} className="text-[#e30613] shrink-0" strokeWidth={1.8} />
          <div>
            <p className="text-[6.5px] tracking-[0.22em] text-white/55">STRONA</p>
            <p className="text-[9px] font-bold mt-[0.5mm]">lstoit.pl</p>
          </div>
        </div>
      </div>

      {/* Rejestrowe */}
      <p className="mt-[3mm] text-[6.5px] text-[#777] text-center tracking-wide">
        LE SEPTIEME TOIT · NIP 9241851371 · REGON 369715569
      </p>
    </div>
  </div>
);

/* ------------------------- WIZYTÓWKA 85×55 ------------------------- */

const CardFront = () => (
  <div className="bcard bg-[#0b0b0b] text-white relative overflow-hidden">
    {/* Red accent corner */}
    <div className="absolute top-0 left-0 w-[22mm] h-[1.2mm] bg-[#e30613]" />
    <div className="absolute bottom-0 right-0 w-[22mm] h-[1.2mm] bg-[#e30613]" />

    <div className="h-full flex items-center justify-center p-[6mm]">
      <div className="text-center">
        <div className="flex flex-col leading-none items-center">
          <span className="font-black tracking-[0.22em] text-[26px] text-white">
            LS<span className="text-[#e30613]">\</span>TOIT
          </span>
          <span className="tracking-[0.38em] text-[8px] text-white/65 mt-[2mm]">
            DACHY PŁASKIE
          </span>
        </div>
        <div className="mt-[5mm] inline-block border border-white/25 px-[4mm] py-[1.5mm]">
          <p className="text-[6px] tracking-[0.28em] text-white/80">EPDM · RESITRIX · HERTALAN · TPO · PVC</p>
        </div>
      </div>
    </div>
  </div>
);

const CardBack = () => (
  <div className="bcard bg-white text-[#0b0b0b] relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-[1.2mm] bg-[#e30613]" />

    <div className="h-full flex flex-col justify-between p-[6mm]">
      {/* Top: name + role */}
      <div>
        <p className="text-[7px] tracking-[0.22em] text-[#e30613] font-bold">SPECJALISTA DACHÓW PŁASKICH</p>
        <p className="text-[14px] font-black tracking-wide mt-[1mm]">LE SEPTIEME TOIT</p>
        <p className="text-[7px] text-[#666] tracking-wide mt-[0.5mm]">Hydroizolacje · Tarasy · Obróbki blacharskie</p>
      </div>

      {/* Middle: contact */}
      <div className="space-y-[1.8mm]">
        <div className="flex items-center gap-[2mm]">
          <Phone size={10} className="text-[#e30613] shrink-0" strokeWidth={2} />
          <p className="text-[9.5px] font-bold tracking-wide">+48 797 219 803</p>
        </div>
        <div className="flex items-center gap-[2mm]">
          <Mail size={10} className="text-[#e30613] shrink-0" strokeWidth={2} />
          <p className="text-[9px] font-semibold">kontakt@lstoit.pl</p>
        </div>
        <div className="flex items-center gap-[2mm]">
          <Globe size={10} className="text-[#e30613] shrink-0" strokeWidth={2} />
          <p className="text-[9px] font-semibold">lstoit.pl</p>
        </div>
      </div>

      {/* Bottom: legal */}
      <p className="text-[6px] text-[#888] tracking-wide">
        NIP 9241851371 · REGON 369715569
      </p>
    </div>
  </div>
);

/* ------------------------- PAGE WRAPPER ------------------------- */

const PrintMaterials = () => {
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#eceae5]">
      <style>{`
        .a5 { width: 148mm; height: 210mm; }
        .bcard { width: 85mm; height: 55mm; }
        @media print {
          @page { margin: 0; size: A4 portrait; }
          body { margin: 0; background: white; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; break-after: page; margin: 0 !important; padding: 10mm !important; background: white !important; box-shadow: none !important; }
          .a5, .bcard { box-shadow: none !important; outline: 1px dashed #ccc; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 bg-[#0b0b0b] text-white border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-[12px] tracking-[0.2em] text-white/70 hover:text-white transition-colors">
              ← POWRÓT DO STRONY
            </a>
            <span className="text-white/20">|</span>
            <p className="text-[12px] tracking-[0.22em] font-bold">MATERIAŁY DRUKOWANE</p>
          </div>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-[#e30613] hover:bg-[#c40510] text-white text-[11px] tracking-[0.2em] font-semibold px-5 py-2.5 transition-colors duration-200"
          >
            <Printer size={14} />
            DRUKUJ / ZAPISZ PDF
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <p className="no-print text-[13px] text-[#444] mb-10 max-w-2xl leading-relaxed">
          Gotowe projekty ulotki A5 (148 × 210 mm) i wizytówki (85 × 55 mm) do wydruku.
          Kliknij <strong>DRUKUJ</strong>, wybierz „Zapisz jako PDF" lub prześlij plik bezpośrednio
          do drukarni. Zalecany druk kolorowy, papier kreda 250–350 g/m² (wizytówka), 170 g/m² (ulotka).
        </p>

        {/* Sekcja: ULOTKA */}
        <section className="print-page mb-16">
          <div className="no-print mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-[18px] font-black tracking-wide text-[#0b0b0b]">ULOTKA A5</h2>
              <p className="text-[12px] text-[#666]">148 × 210 mm · front + tył</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="no-print text-[11px] tracking-[0.2em] text-[#666] mb-2">FRONT</p>
              <div className="shadow-2xl"><FlyerFront /></div>
            </div>
            <div>
              <p className="no-print text-[11px] tracking-[0.2em] text-[#666] mb-2">TYŁ</p>
              <div className="shadow-2xl"><FlyerBack /></div>
            </div>
          </div>
        </section>

        {/* Sekcja: WIZYTÓWKA */}
        <section className="print-page">
          <div className="no-print mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-[18px] font-black tracking-wide text-[#0b0b0b]">WIZYTÓWKA</h2>
              <p className="text-[12px] text-[#666]">85 × 55 mm · front + tył</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-8 items-start">
            <div>
              <p className="no-print text-[11px] tracking-[0.2em] text-[#666] mb-2">FRONT</p>
              <div className="shadow-2xl"><CardFront /></div>
            </div>
            <div>
              <p className="no-print text-[11px] tracking-[0.2em] text-[#666] mb-2">TYŁ</p>
              <div className="shadow-2xl"><CardBack /></div>
            </div>
          </div>

          <div className="no-print mt-10 max-w-2xl text-[12.5px] text-[#444] leading-relaxed border-t border-[#0b0b0b]/10 pt-6">
            <p className="font-bold text-[#0b0b0b] mb-2 tracking-wide">WSKAZÓWKI DO DRUKU</p>
            <ul className="space-y-1.5 list-disc pl-5">
              <li>Wizytówka: papier matowy / półmatowy kreda <strong>350 g/m²</strong>, wykończenie folia soft-touch lub laminat matowy (dla eleganckiego wyczucia).</li>
              <li>Ulotka: papier kreda <strong>170 g/m²</strong> matowa, druk dwustronny 4+4 CMYK.</li>
              <li>Jeśli drukarnia wymaga <strong>spadu (bleed)</strong>: dodaj 3 mm z każdej strony — mogę przygotować wersję ze spadem na życzenie.</li>
              <li>Kolor akcentu: <strong>#e30613</strong> (CMYK ~ 0 / 96 / 91 / 11) — upewnij się, że drukarnia dokładnie tym kolorem odwzoruje.</li>
              <li>Logo pozostaje jako tekst (fontem Inter Black) — wtopione, nie wymaga rasteryzacji.</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrintMaterials;
